import { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Navbarefixed } from "./Component/Navbarefixed";
import { Preloader } from "./Utility/Preloader";
import { HomePage } from "./Pages/HomePage";
import { Footer } from "./Component/Footer";
import { FloatingButtons } from "./Component/FloatingButtons";
// اللغة من الرابط: "/" للإنجليزي و "/ar" و "/ru" و "/de" و "/fr" و "/es" للباقي
import { BASENAME } from "./Utility/Languagecontext";
import { LENIS_OPTIONS, setLenis } from "./Utility/scroll";
import { stripLang } from "./Utility/i18nPaths";
import { Tracker } from "./Utility/tracking";

// لوحة التحكم (/admin) بتتحمّل لوحدها ومن غير النافبار والفوتر
const AdminApp = lazy(() => import("./Admin/AdminApp"));
const IS_ADMIN = typeof window !== "undefined" && stripLang(window.location.pathname).startsWith("/admin");

// باقي الصفحات بتتحمّل وقت ما الزائر يفتحها بس (الصفحة الرئيسية بتفتح أسرع)
const page = (load, name) => lazy(() => load().then((m) => ({ default: m[name] })));
const AboutUsPage = page(() => import("./Pages/AboutUsPage"), "AboutUsPage");
const BlogsPage = page(() => import("./Pages/BlogsPage"), "BlogsPage");
const ContactUsPage = page(() => import("./Pages/ContactUsPage"), "ContactUsPage");
const ProjectsPage = page(() => import("./Pages/ProjectsPage"), "ProjectsPage");
const DeveloperPage = page(() => import("./Pages/DevelopersInfo_Page"), "DevelopersInfo_Page");
const OffPlanGuidePage = page(() => import("./Pages/OffPlanGuidePage"), "OffPlanGuidePage");

// بعد كل تنقّل: لو فيه #hash ننزل على القسم، غير كده نرجع لأول الصفحة
const ScrollManager = ({ lenisRef }) => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    const lenis = lenisRef.current;
    const timer = setTimeout(() => {
      // الصفحة الجديدة اتعملها render، فنحدّث طول الصفحة الأول
      lenis?.resize();
      if (hash) {
        const el = document.getElementById(decodeURIComponent(hash.slice(1)));
        if (el) {
          if (lenis) lenis.scrollTo(el, { duration: 1.4 });
          else el.scrollIntoView({ behavior: "smooth" });
          return;
        }
      }
      if (lenis) lenis.scrollTo(0, { immediate: true });
      else window.scrollTo(0, 0);
    }, 60);
    return () => clearTimeout(timer);
  }, [pathname, hash, lenisRef]);

  return null;
};

// لما صفحة تتحمّل متأخر شوية، نحدّث طول الصفحة عشان السكرول الناعم يشتغل صح
const ResizeOnMount = ({ lenisRef }) => {
  useEffect(() => {
    lenisRef.current?.resize();
  }, [lenisRef]);
  return null;
};

const SKIP_INTRO =
  IS_ADMIN || (typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches);

// الهيرو بيقرا الإشارة دي من <html>: "wait" = الـ Preloader ظاهر، "go" = ابدأ أنيميشن الدخول
// بتتحط هنا على مستوى الملف عشان تبقى جاهزة قبل أول render للهيرو
if (typeof document !== "undefined") {
  document.documentElement.setAttribute("data-intro", SKIP_INTRO ? "go" : "wait");
}

function SiteApp() {
  const lenisRef = useRef(null);

  // الـ Preloader بيظهر مرة واحدة أول ما الموقع يفتح (ومش بيظهر لو المستخدم مفعّل تقليل الحركة)
  const [loading, setLoading] = useState(!SKIP_INTRO); // السكرول مقفول لحد ما الفتحة تخلص
  const finishLoading = useCallback(() => setLoading(false), []);
  const startIntro = useCallback(() => document.documentElement.setAttribute("data-intro", "go"), []);

  // Smooth scroll ناعم يخلي كل الأنيميشن بتاع السكرول سلس (الإعدادات في Utility/scroll.js)
  useEffect(() => {
    if (SKIP_INTRO) return;
    const lenis = new Lenis(LENIS_OPTIONS);
    lenisRef.current = lenis;
    setLenis(lenis);
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      lenisRef.current = null;
      setLenis(null);
    };
  }, []);

  // قفل السكرول طول ما الـ Preloader ظاهر (لازم يجي بعد effect الـ Lenis)
  useEffect(() => {
    document.documentElement.style.overflow = loading ? "hidden" : "";
    const lenis = lenisRef.current;
    if (lenis) {
      if (loading) lenis.stop();
      else lenis.start();
    }
  }, [loading]);

  const lazyPage = (el) => (
    <>
      {el}
      <ResizeOnMount lenisRef={lenisRef} />
    </>
  );

  return (
    <BrowserRouter basename={BASENAME}>
      <ScrollManager lenisRef={lenisRef} />
      <Tracker />
      {loading && <Preloader onOpen={startIntro} onDone={finishLoading} />}
      <div className="App">
        <Navbarefixed />
        <FloatingButtons />
        <Suspense fallback={<div style={{ minHeight: "100vh" }} />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about-us" element={lazyPage(<AboutUsPage />)} />
            <Route path="/projects" element={lazyPage(<ProjectsPage />)} />
            {/* "View project" في الهوم بيفتح صفحة المشاريع وبينزل على المشروع ده */}
            <Route path="/project/:id" element={lazyPage(<ProjectsPage />)} />
            <Route path="/developers/:slug" element={lazyPage(<DeveloperPage />)} />
            <Route path="/blogs" element={lazyPage(<BlogsPage />)} />
            <Route path="/contact-us" element={lazyPage(<ContactUsPage />)} />
            <Route path="/off-plan-guide" element={lazyPage(<OffPlanGuidePage />)} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
        <Footer />
      </div>
    </BrowserRouter>
  );
}

const AdminRoot = () => (
  <BrowserRouter basename={BASENAME}>
    <Suspense fallback={<div style={{ minHeight: "100vh", background: "#080808" }} />}>
      <AdminApp />
    </Suspense>
  </BrowserRouter>
);

const App = IS_ADMIN ? AdminRoot : SiteApp;

export default App;
